/**
 * READING A TEST SCRIPT'S RESULT OFF ITS OWN "EXECUTION SUMMARY" SHEET.
 *
 * A UAT test script written in Excel carries an Execution Summary sheet — a plain
 * label/value table (Vinnie's template): Total Test Cases / Passed / Failed /
 * Open Defects, each with a number beside it. When the external tester uploads
 * the completed workbook, their browser parses this sheet (where the xlsx library
 * already lives) and sends the figures, so the signed record can show a pass/fail
 * read-out without anyone opening Excel.
 *
 * The labels are MATCHED, not read from fixed cells, so a template whose rows are
 * reordered or lightly renamed still reads correctly. It is a read-out, never a
 * control — a tester could type any number into their own sheet — so a sheet this
 * cannot make sense of simply yields null and the record carries no read-out.
 *
 * Kept out of the component so it can be proven at commit time (test-exec-summary)
 * against a grid that mirrors the real template, with no spreadsheet on disk.
 */
export type ExecSummary = { cases: number; passed: number; failed: number; defects: number };

/** The first value cell in a row that is a non-negative whole number — the figure
 *  beside a label. Booleans and blanks are skipped; a percentage like 0.92 floors
 *  to 0, which is why completion % is derived from passed/cases elsewhere, never
 *  read from the sheet. */
const firstNumber = (cells: unknown[]): number | null => {
  for (const c of cells) {
    if (typeof c === 'boolean' || c === '' || c == null) continue;
    const n = Number(c);
    if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
  }
  return null;
};

/**
 * The execution summary read from a sheet already turned into a row-major grid
 * (`XLSX.utils.sheet_to_json(ws, { header: 1 })`). Null when nothing matched.
 */
export function summaryFromGrid(grid: unknown[][]): ExecSummary | null {
  let cases: number | null = null;
  let passed: number | null = null;
  let failed: number | null = null;
  let defects: number | null = null;
  for (const row of grid) {
    if (!Array.isArray(row)) continue;
    const label = String(row[0] ?? '').trim().toLowerCase();
    if (!label) continue;
    const value = firstNumber(row.slice(1));
    if (value === null) continue;
    if (cases === null && /(total.*case|test case|^total$)/.test(label)) cases = value;
    else if (passed === null && /^pass/.test(label)) passed = value;
    else if (failed === null && /^fail/.test(label)) failed = value;
    else if (defects === null && /defect/.test(label)) defects = value;
  }
  if (cases === null && passed === null && failed === null && defects === null) return null;
  return { cases: cases ?? 0, passed: passed ?? 0, failed: failed ?? 0, defects: defects ?? 0 };
}

/** Percent of cases that passed, floored into 0–100 for the pass/fail bar. */
export const pctPassed = (s: ExecSummary): number =>
  s.cases > 0 ? Math.min(100, Math.round((s.passed / s.cases) * 100)) : 0;

/**
 * THE ONE SET OF LABELS for an execution summary, so the tester's page and the
 * in-app record can never drift (copy-witness, 5 Nov 2026). Title Case; the
 * in-app inline read lower-cases them. The percentage is a "Pass rate" — a
 * distinct label from the "Passed" COUNT beside it, so two adjacent numbers are
 * never both called "Passed".
 */
export const EXEC_LABELS = {
  cases: 'Cases',
  passed: 'Passed',
  failed: 'Failed',
  defects: 'Defects',
  passRate: 'Pass rate',
} as const;
